import React, { useState, useMemo } from 'react'
import { Typography, ListItemButton, IconButton, Collapse, Box, Chip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Stack } from '@mui/system'
import { useParams } from 'react-router-dom'

import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import CircleIcon from '@mui/icons-material/Circle'

import useFocus from 'components/AuthenticatedLayout/Header/AccountsPerimeter/use-focus'
import { useNavigate } from 'router'
import { availableToColor, LIST_UP_COLOR } from 'pages/Supervision/helpers'

function Accounts({ account, total, onSiteClick, onBuildingClick }) {
  const {focusId} = useParams()
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(account.expanded)
  const isBuildings = account.sites.every(site => site.isSurfaceArea)
  const labelKey = `cartography.maps.legend.${isBuildings ? 'building' : 'site'}${total > 1 ? '_others' : ''}`
  
  const displayName = isBuildings 
    ? account.sites[0]?.parentSiteName
    : account.name
  return (
    <Stack direction="column" sx={{ minWidth: '300px' }}>
      <Stack
        alignItems="center"
        justifyContent="space-between"
        direction="row"
        gap={2}
        sx={{
          color: 'content.tertiary',
          minHeight: '50px',
          marginBottom: 1,
          px: 1,
          cursor: 'pointer'
        }}
        onClick={() => setExpanded(!expanded)}>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '18px',
            color: 'content.primary'
          }}>
          {displayName}
        </Typography>
        <Stack direction="row" gap={1} alignItems="center">
          <Chip
            sx={{ px: 1, color: 'content.secondary' }}
            variant="outlined"
            label={`${total} ${t(labelKey)}`}
            size="small"
          />
          <Box>{expanded ? <ExpandLess /> : <ExpandMore />}</Box>
        </Stack>
      </Stack>
      <Stack direction="column">
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {account.sites.map(
            site => (
                <ListItemButton
                  key={`${site.sf_id}|${site.buildingId}`}
                  onClick={() => onSiteClick(site)}
                  disableGutters
                  selected={site.sf_id === focusId}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    color: 'content.primary',
                    minHeight: '30px',
                    borderRadius: '6px',
                    px: 1,
                    '&.Mui-selected': {
                      color: 'primary.main'
                    },
                    '&:hover': {
                      backgroundColor: '#F6F7F8'
                    }
                  }}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0.5,
                      alignItems: 'center',
                      fontWeight: 500,
                      fontSize: '12px',
                      lineHeight: '18px',
                      color: 'content.primary',
                      flex: 1,
                      minWidth: 0
                    }}>
                    <CircleIcon
                      sx={{
                        color: site.equipment.available_level === 'up' ? LIST_UP_COLOR : availableToColor(site.equipment.available_level),
                        fontSize: '10px',
                        flexShrink: 0
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                      {site?.name}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={e => onBuildingClick(e, site)}
                    sx={{
                      fontSize: '16px',
                      color: 'content.secondary',
                      flexShrink: 0,
                      padding: '4px'
                    }}>
                    <i className="fa-light fa-buildings"></i>
                  </IconButton>
                </ListItemButton>
              )
          )}
        </Collapse>
      </Stack>
    </Stack>
  )
}

function TabsContent({ sites, onSiteSelect, onBuildingSelect }) {
  const accountsParent = useMemo(() => {
    return sites
      .reduce((acc, { account_parent, ...site }) => {
        const idx = acc.findIndex(({ id }) => id === account_parent.id)
        if (idx === -1) {
          return [
            ...acc,
            {
              id: account_parent.id,
              name: account_parent.name,
              expanded: false,
              sites: [site]
            }
          ]
        } else {
          acc[idx].sites.push(site)
          return acc
        }
      }, [])
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((accountParent, i) => ({
        ...accountParent,
        expanded: i === 0,
        sites: accountParent.sites.sort((a, b) => a.name.localeCompare(b.name))
      }))      
  }, [sites])
  return (
    <Box 
      sx={{ 
        overflowY: 'auto',
        overflowX: 'hidden',
        flex: 1,
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }}
    >
      <Stack direction="column">
        {accountsParent.map(account => (
          <Accounts
            key={account.id}
            account={account}
            total={account.sites.length}
            onSiteClick={onSiteSelect}
            onBuildingClick={(e, site) => {
              e.stopPropagation()
              onBuildingSelect(site)
            }}
          />
        ))}
      </Stack>
    </Box>
  )
}

const AccountsList = ({ sites, onClick }) => {
  const navigate = useNavigate()
  const {onFocus} = useFocus()
  return (
    <Box
      sx={{
        cursor: 'pointer',
        borderTopLeftRadius: '24px',
        borderBottomLeftRadius: '24px',
        border: '1px solid #E5E7EB',
        borderRight: 0,
        padding: 2,
        minWidth: '300px',
        width: 'auto',
        maxWidth: '400px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <TabsContent
        onSiteSelect={(site) => {
          onClick(site)
        }}
        onBuildingSelect={(site) => {
          onFocus(site)
          navigate({
            pathname: `/monitoring/map/${site.sf_id}/redirect_first_floor/${site.buildingId || ''}`,
            addSite: false,
          })
        }}
        sites={sites}
      />
    </Box>
  )
}

export default AccountsList